// =============================================================================
// SIREPPAR - Wizard Candidatura, Paso 6: Revisión y Envío
// =============================================================================

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { candidatoService, convocatoriaService } from '../../../services/api';
import type { Candidato } from './CandidaturaWizardPage';

interface Props {
  candidato: Candidato;
  onSaved: () => void;
  onBack: () => void;
  onEditar: (paso: number) => void;
}

interface ResultadoValidacion {
  clave: string;
  label: string;
  cumple: boolean;
  detalle?: string;
}
const DESCRIPCIONES_VALIDACION: Record<string, string> = {
  datosPersonales: 'Todos los campos obligatorios están cargados.',
  dni: 'Frente y dorso del documento de identidad verificados.',
  foto: 'La fotografía cumple los requisitos técnicos exigidos.',
  domicilio: 'Domicilio completo y comprobante de residencia adjunto.',
  aceptacion: 'Formulario de aceptación de candidatura cargado.',
  antecedentes: 'Certificado de antecedentes penales (Nación) cargado.',
  plataforma: 'Plataforma electoral cargada.',
  actaProclamacion: 'Acta de proclamación cargada.',
  declaracionJurada: '9 supuestos del Art. 72 declarados + candidatura única + declaración final.',
  candidaturaUnica: 'El candidato no figura presentado por otra agrupación en esta convocatoria.',
};

const DOCUMENTOS_ART77 = [
  { tipo: 'ACEPTACION_CANDIDATURA', titulo: 'Aceptación de candidatura' },
  { tipo: 'ANTECEDENTES_PENALES_CANDIDATO', titulo: 'Antecedentes penales' },
  { tipo: 'PLATAFORMA_ELECTORAL_CANDIDATURA', titulo: 'Plataforma electoral' },
  { tipo: 'ACTA_PROCLAMACION', titulo: 'Acta de proclamación' },
];

function formatFecha(fecha: string | null): string {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleDateString('es-AR');
}

export default function Paso6RevisionEnvio({ candidato, onBack, onEditar }: Props) {
  const navigate = useNavigate();
  const [confirmoEnvio, setConfirmoEnvio] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<{ numeroExpediente: string; codigoVerificacion: string } | null>(null);
  const [descargando, setDescargando] = useState(false);

  const { data, isLoading } = useQuery<{ validaciones: ResultadoValidacion[]; puedeEnviar: boolean }>({
    queryKey: ['candidato-validacion', candidato.id],
    queryFn: async () => (await candidatoService.obtenerValidacion(candidato.id)).data.data,
  });

  const { data: estadoConv } = useQuery({
    queryKey: ['apoderado-mi-estado'],
    queryFn: async () => (await convocatoriaService.getMiEstado()).data.data,
  });

  const cierreCandidaturas: string | undefined = estadoConv?.convocatoria?.plazos?.cierre_candidaturas;
  const plazoVencido = (() => {
    if (!cierreCandidaturas) return false;
    const [anio, mes, dia] = cierreCandidaturas.slice(0, 10).split('-').map(Number);
    const finDelCierre = new Date(anio, mes - 1, dia, 23, 59, 59, 999);
    return new Date() > finDelCierre;
  })();
  const formatFechaPlazo = (fecha: string) => {
    const [anio, mes, dia] = fecha.slice(0, 10).split('-');
    return `${dia}/${mes}/${anio}`;
  };

  const validaciones = data?.validaciones || [];
  const puedeEnviar = (data?.puedeEnviar ?? false) && confirmoEnvio && !plazoVencido;

  const enviar = async () => {
    setEnviando(true);
    try {
      const res = await candidatoService.enviar(candidato.id);
      setResultado(res.data.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo enviar el expediente');
    } finally {
      setEnviando(false);
    }
  };

  const descargarComprobante = async () => {
    setDescargando(true);
    try {
      const res = await candidatoService.descargarPdf(candidato.id);
      const blobUrl = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Constancia_Candidatura_${resultado?.numeroExpediente || candidato.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('No se pudo descargar el comprobante');
    } finally {
      setDescargando(false);
    }
  };

  const docCargado = (tipo: string) => candidato.documentos.find(d => d.tipo === tipo);

  if (resultado) {
    return (
      <div className="max-w-lg mx-auto text-center py-10">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl text-green-700">✓</div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Expediente presentado exitosamente</h2>
        <p className="text-sm text-gray-600 mb-6">Le hemos enviado un email de confirmación con el PDF adjunto.</p>

        <div className="bg-white rounded-xl shadow p-6 text-left space-y-3 mb-6">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Número de expediente</div>
            <div className="text-lg font-bold text-gray-900">{resultado.numeroExpediente}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Código de verificación</div>
            <div className="text-lg font-mono font-bold text-blue-700">{resultado.codigoVerificacion}</div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={descargarComprobante}
            disabled={descargando}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
          >
            {descargando ? 'Descargando...' : 'Descargar comprobante'}
          </button>
          <button
            onClick={() => navigate('/apoderado/panel')}
            className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
          >
            Volver al panel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
        <strong>Última oportunidad para editar.</strong> Una vez enviada, la lista queda en manos de la Secretaría Electoral Permanente. Solo vas a poder modificarla si te llega una observación formal (Art. 79 CE) durante el plazo de subsanación.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1">Agrupación</div>
          <div className="font-semibold text-gray-900">{candidato.partido?.denominacion || '—'}</div>
          <div className="text-xs text-gray-500 mt-1">{candidato.partido?.tipo}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1">Candidatura</div>
          <div className="font-semibold text-gray-900">{candidato.cargo}</div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Datos del candidato</h3>
          <button onClick={() => onEditar(1)} className="text-xs text-blue-600 hover:text-blue-800 underline">Editar</button>
        </div>
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Apellido y nombre</td><td className="py-2 text-right font-medium">{candidato.apellido}, {candidato.nombre}</td></tr>
            <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">DNI</td><td className="py-2 text-right font-medium">{candidato.dni}</td></tr>
            <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Fecha de nacimiento</td><td className="py-2 text-right font-medium">{formatFecha(candidato.fechaNacimiento)}</td></tr>
            <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Género</td><td className="py-2 text-right font-medium">{candidato.genero || '—'}</td></tr>
            <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Nacionalidad</td><td className="py-2 text-right font-medium">{candidato.nacionalidad || '—'}</td></tr>
            <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Domicilio</td><td className="py-2 text-right font-medium">{candidato.domicilioCalle} {candidato.domicilioNumero}, {candidato.localidad}</td></tr>
            <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Residencia declarada</td><td className="py-2 text-right font-medium">{candidato.anosResidenciaDeclarada ?? '—'} años {candidato.mesesResidenciaDeclarada ? `y ${candidato.mesesResidenciaDeclarada} meses` : ''}</td></tr>
            <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Cargo</td><td className="py-2 text-right font-medium">{candidato.cargo}</td></tr>
            <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Nombre en boleta</td><td className="py-2 text-right font-medium">{candidato.nombreEnBoleta || '—'}</td></tr>
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Documentación adjunta</h3>
          <button onClick={() => onEditar(4)} className="text-xs text-blue-600 hover:text-blue-800 underline">Editar</button>
        </div>
        <div className="text-sm divide-y divide-gray-100">
          {DOCUMENTOS_ART77.map(doc => {
            const cargado = docCargado(doc.tipo);
            return (
              <div key={doc.tipo} className="py-2 flex items-center justify-between">
                <span>📄 {doc.titulo}</span>
                {cargado ? (
                  <span className="text-green-700 text-xs">✓ Cargado</span>
                ) : (
                  <span className="text-red-600 text-xs">Pendiente</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Validaciones automáticas del sistema</h3>
        {isLoading ? (
          <p className="text-sm text-gray-500">Verificando...</p>
        ) : (
          <div className="space-y-2">
            {validaciones.map(v => (
              <div
                key={v.clave}
                className={`flex items-start gap-3 p-3 rounded-lg border ${!v.cumple ? 'bg-red-50 border-red-200' : v.detalle ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}
              >
                <span className={!v.cumple ? 'text-red-600' : v.detalle ? 'text-amber-600' : 'text-green-700'}>{!v.cumple ? '✗' : v.detalle ? '⚠' : '✓'}</span>
                <div>
                  <div className={`text-sm font-medium ${!v.cumple ? 'text-red-700' : v.detalle ? 'text-amber-800' : 'text-green-800'}`}>{v.label}</div>
                  <div className={`text-xs mt-0.5 ${!v.cumple ? 'text-red-600' : v.detalle ? 'text-amber-700' : 'text-gray-500'}`}>
                    {v.detalle || DESCRIPCIONES_VALIDACION[v.clave]}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {plazoVencido && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          El plazo para registrar candidaturas venció el <strong>{formatFechaPlazo(cierreCandidaturas!)}</strong> (Art. 72, Ley XII-21). No es posible presentar la solicitud.
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-3">Firma electrónica y envío</h3>
        <p className="text-sm text-blue-800 mb-4">
          Al confirmar el envío, el sistema genera un número de expediente único y notifica a la Secretaría Electoral Permanente. Vas a recibir por correo un comprobante con el PDF adjunto.
        </p>
        <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-blue-100 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmoEnvio}
            onChange={e => setConfirmoEnvio(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0"
          />
          <span className="text-sm">
            <strong>Confirmo el envío</strong> de la candidatura de {candidato.apellido}, {candidato.nombre} para el cargo de {candidato.cargo}, en representación de {candidato.partido?.denominacion}.
          </span>
        </label>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">← Volver</button>
        <button
          onClick={enviar}
          disabled={!puedeEnviar || enviando}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium"
        >
          {enviando ? 'Enviando...' : 'Firmar y enviar expediente'}
        </button>
      </div>
    </div>
  );
}
